import { useNavigate } from 'react-router-dom'

import { Box, Form, Grid } from '@factorialco/deprecated-design-system'
import { F0Button, F0DatePicker } from '@factorialco/f0-react'
import { F0Select } from '@factorialco/f0-react'
import {
  DatePickerValue,
  Input,
  NumberInput,
  OneModal,
} from '@factorialco/f0-react/dist/experimental'
import {
  AtsCreateJobPostingModalDocument,
  AtsJobPostingStatusesEnum,
  AtsJobRequirementsOptionsEnum,
  AtsNewJobPostingModalDocument,
  AtsNewJobPostingModalLegalEntitiesFragment,
  AtsNewJobPostingModalLocationsFragment,
  AtsNewJobPostingModalTeamsFragment,
  AtsWorkplaceTypesEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { EventTypes, dispatch } from 'modules/core/lib/pubSub'
import app from 'modules/core/store/models/app'

import { JobCatalogRoleSelector } from 'modules/ats/components/job_catalog/JobCatalogRoleSelector'

import { NewJobPostingModalContentSkeleton } from './Skeleton'

interface NewJobPostingForm {
  authorId: number
  companyId: number
  coverLetterRequirement: AtsJobRequirementsOptionsEnum
  cvRequirement: AtsJobRequirementsOptionsEnum
  jobCatalogNodeRolesUuid: string | null
  jobCatalogNodeLevelsUuid: string | null
  legalEntityId: number
  locationId: number
  personalUrlRequirement: AtsJobRequirementsOptionsEnum
  phoneRequirement: AtsJobRequirementsOptionsEnum
  photoRequirement: AtsJobRequirementsOptionsEnum
  numberOfVacancies: number | null
  startDate: string | null
  status: AtsJobPostingStatusesEnum
  teamId: number | undefined
  title: string
  workplaceType: AtsWorkplaceTypesEnum | undefined
}

const NewJobPostingModalPage = () => {
  const { company, access } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const { data: jobData } = useQuery(AtsNewJobPostingModalDocument, {
    variables: {
      companyId: company?.id,
    },
  })

  const atsCompanyId = jobData?.ats.companiesConnection.nodes[0]?.id ?? 0 // How to do it correctly?

  const [createJobPosting] = useMutation(AtsCreateJobPostingModalDocument, {
    extractorFn: (data) => data.atsMutations.createAtsJobPosting,
  })

  const isJobPostingRevampFeatureEnabled =
    jobData?.features.featuresConnection.nodes.find(
      (feature) => feature.name === 'dev_ats_job_posting_revamp'
    )?.enabled ?? false

  const form = useForm<NewJobPostingForm>({
    defaultValues: {
      authorId: access?.id,
      companyId: company?.id,
      coverLetterRequirement: 'optional',
      cvRequirement: 'optional',
      jobCatalogNodeRolesUuid: null,
      jobCatalogNodeLevelsUuid: null,
      legalEntityId: 0,
      locationId: 0,
      personalUrlRequirement: 'optional',
      phoneRequirement: 'optional',
      photoRequirement: 'optional',
      numberOfVacancies: null,
      startDate: null,
      status: 'draft',
      teamId: undefined,
      title: '',
      workplaceType: undefined,
    },
    validateOnChange: false,
    validateOnBlur: false,
    validate(values) {
      const errors: FormErrors<NewJobPostingForm> = {}
      if (values.title === '') {
        errors.title = i18n.t('common.validations.empty')
      }

      if (!values.locationId) {
        errors.locationId = i18n.t('common.validations.empty')
      }

      if (!values.legalEntityId) {
        errors.legalEntityId = i18n.t('common.validations.empty')
      }

      if (values.workplaceType === undefined) {
        errors.workplaceType = i18n.t('common.validations.empty')
      }

      if (values.numberOfVacancies && values.numberOfVacancies <= 0) {
        errors.numberOfVacancies = i18n.t('ats.jobs.invalid_number_of_vacancies')
      }

      return errors
    },
    async onSubmit(values, { setErrors }) {
      const result = await createJobPosting({
        variables: {
          authorId: access?.id,
          companyId: company?.id,
          coverLetterRequirement: 'optional',
          cvRequirement: 'optional',
          jobCatalogNodeRolesUuid: values.jobCatalogNodeRolesUuid,
          jobCatalogNodeLevelsUuid: values.jobCatalogNodeLevelsUuid,
          legalEntityId: Number(values.legalEntityId),
          locationId: Number(values.locationId),
          numberOfVacancies: values.numberOfVacancies,
          personalUrlRequirement: 'optional',
          phoneRequirement: 'optional',
          photoRequirement: 'optional',
          startDate: values.startDate,
          status: 'draft',
          teamId: Number(values.teamId),
          title: values.title,
          workplaceType: values.workplaceType,
        },
      })

      const handled = handleResult(result, (errors) => setErrors(errors))

      track(trackingEvents.ats.createJobOpeningSubmit, {
        is_successful: handled.success,
        error_code: !handled.success && result.toString(),
        ...form.values,
        ats_company_id: atsCompanyId,
      })

      if (handled.success && handled.data?.jobPosting?.id) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

        track(trackingEvents.ats.jobSettingsJobCreateModalNext)
        dispatch({ type: EventTypes.JOB_POSTING_CREATED })

        navigate(
          navigation.ats.jobs.jobsId.settings.fullPath({ jobId: handled.data.jobPosting.id })
        )
      }

      return handled
    },
  })

  const onClose = () => {
    track(trackingEvents.ats.createJobOpeningCancel)
    navigate(navigation.ats.jobs.fullPath())
  }

  const legalEntities = jobData?.companies.legalEntities ?? []
  const locations = jobData?.locations.locations ?? []
  const teams = jobData?.teams.teams ?? []
  const jobCatalogRoles = jobData?.jobCatalog.treeNodesConnection.nodes ?? []

  const startDatePickerValue = (() => {
    if (!form.values.startDate) {
      return undefined
    }

    const parsedDate = stringToDate(form.values.startDate, 'backend')

    if (!parsedDate.isValid()) {
      return undefined
    }

    const normalizedDate = parsedDate.toDate()

    return {
      granularity: 'day' as const,
      value: {
        from: normalizedDate,
        to: normalizedDate,
      },
    }
  })()

  const handleStartDateChange = (date: DatePickerValue | undefined) => {
    const selectedDate = date?.value?.from

    if (!selectedDate) {
      form.setFieldValue('startDate', null)
      return
    }

    form.setFieldValue('startDate', i18nDateToString(selectedDate, 'backend'))
  }

  const loading = !jobData

  return (
    <OneModal onClose={onClose} isOpen>
      <OneModal.Header title={i18n.t('ats.jobs.new_job_modal.title')} />
      <OneModal.Content withPadding>
        {loading ? (
          <NewJobPostingModalContentSkeleton />
        ) : (
          <Form padding={null}>
            <Box width="full" gap="s16">
              <Input
                label={i18n.t('ats.jobs.new_job_modal.title_input_label')}
                name="jobTitle"
                placeholder={i18n.t('ats.jobs.new_job_modal.title_input_placeholder')}
                size="md"
                onChange={(value) => form.setFieldValue('title', value)}
                value={form.values.title}
                error={form.errors.title}
              />
              {isJobPostingRevampFeatureEnabled && jobCatalogRoles.length > 0 && (
                <JobCatalogRoleSelector
                  onLevelSelect={(roleUuid, roleLevelUuid) => {
                    form.setValues({
                      ...form.values,
                      jobCatalogNodeRolesUuid: roleUuid ?? null,
                      jobCatalogNodeLevelsUuid: roleLevelUuid ?? null,
                    })
                  }}
                  jobCatalogRoles={jobCatalogRoles}
                />
              )}
              <Grid variant="fixed">
                <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                  <F0Select
                    label={i18n.t('ats.jobs.new_job_modal.workplace_input_label')}
                    name="workplace"
                    placeholder={i18n.t('ats.jobs.new_job_modal.workplace_input_placeholder')}
                    onChange={(value: string) => form.setFieldValue('locationId', value)}
                    size="md"
                    value={form.values.locationId ? form.values.locationId.toString() : undefined}
                    options={locations.map((location: AtsNewJobPostingModalLocationsFragment) => ({
                      label: location.name,
                      value: location.id.toString(),
                    }))}
                    searchBoxPlaceholder={i18n.t(
                      'ats.jobs.new_job_modal.workplace_search_placeholder'
                    )}
                    searchEmptyMessage={i18n.t('ats.jobs.new_job_modal.search_no_results_found')}
                    error={form.errors.locationId}
                  />
                </Box>
                <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                  <F0Select
                    label={i18n.t('ats.jobs.new_job_modal.team_input_label')}
                    name="team"
                    placeholder={i18n.t('ats.jobs.new_job_modal.team_input_placeholder')}
                    onChange={(value: string) => form.setFieldValue('teamId', value)}
                    size="md"
                    value={form.values.teamId ? form.values.teamId.toString() : undefined}
                    options={teams.map((team: AtsNewJobPostingModalTeamsFragment) => ({
                      label: team.name,
                      value: team.id.toString(),
                    }))}
                    searchBoxPlaceholder={i18n.t('ats.jobs.new_job_modal.team_search_placeholder')}
                    searchEmptyMessage={i18n.t('ats.jobs.new_job_modal.search_no_results_found')}
                    error={form.errors.teamId}
                  />
                </Box>
              </Grid>

              <Grid variant="fixed">
                <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                  <NumberInput
                    label={i18n.t('ats.jobs.new_job_modal.number_of_vacancies')}
                    locale="en-US"
                    name="numberOfVacancies"
                    onChange={(value) =>
                      form.setFieldValue('numberOfVacancies', value === null ? null : Number(value))
                    }
                    placeholder="1"
                    size="md"
                    value={form.values.numberOfVacancies}
                    error={form.errors.numberOfVacancies}
                  />
                </Box>
                <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                  <F0DatePicker
                    label={i18n.t('ats.jobs.new_job_modal.start_date')}
                    onChange={handleStartDateChange}
                    size="md"
                    value={startDatePickerValue}
                    placeholder={i18n.t('ats.jobs.new_job_modal.start_date_placeholder')}
                  />
                </Box>
              </Grid>

              <Grid variant="fixed">
                <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                  <F0Select
                    label={i18n.t('ats.jobs.new_job_modal.legal_entity_input_label')}
                    name="legalEntity"
                    placeholder={i18n.t('ats.jobs.new_job_modal.legal_entity__input_placeholder')}
                    onChange={(value: string) => form.setFieldValue('legalEntityId', value)}
                    size="md"
                    value={
                      form.values.legalEntityId ? form.values.legalEntityId?.toString() : undefined
                    }
                    options={legalEntities.map(
                      (legalEntity: AtsNewJobPostingModalLegalEntitiesFragment) => ({
                        label: `${legalEntity.legalName} - ${legalEntity.currency}`,
                        value: legalEntity.id.toString(),
                      })
                    )}
                    searchBoxPlaceholder={i18n.t(
                      'ats.jobs.new_job_modal.legal_entity__search_placeholder'
                    )}
                    searchEmptyMessage={i18n.t('ats.jobs.new_job_modal.search_no_results_found')}
                    error={form.errors.legalEntityId}
                  />
                </Box>
                <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                  <F0Select
                    label={i18n.t('ats.jobs.new_job_modal.workplace_type_input_label')}
                    name="locationType"
                    placeholder={i18n.t('ats.jobs.new_job_modal.workplace_type_input_placeholder')}
                    onChange={(value: AtsWorkplaceTypesEnum) =>
                      form.setFieldValue('workplaceType', value)
                    }
                    size="md"
                    value={form.values.workplaceType}
                    error={form.errors.workplaceType}
                    options={[
                      {
                        label: i18n.t('ats.workplace_types.onsite'),
                        value: 'onsite',
                      },
                      {
                        label: i18n.t('ats.workplace_types.hybrid'),
                        value: 'hybrid',
                      },
                      {
                        label: i18n.t('ats.workplace_types.remote'),
                        value: 'remote',
                      },
                    ]}
                  />
                </Box>
              </Grid>
            </Box>
          </Form>
        )}
      </OneModal.Content>
      <OneModal.Footer>
        <Box flexDirection="row" justifyContent="flexEnd" gap="s12" width="full">
          <F0Button onClick={onClose} label={i18n.t('common.cancel')} variant="outline" />
          <F0Button
            onClick={() => form.handleSubmit()}
            label={i18n.t('ats.jobs.new_job_modal.create_button')}
            loading={form.isSubmitting}
          />
        </Box>
      </OneModal.Footer>
    </OneModal>
  )
}

export default NewJobPostingModalPage
